import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Alert } from '../../components/Alert';
import { format } from 'date-fns';
import clsx from 'clsx';

interface User {
  id: string;
  username: string;
  email: string;
  credits: number;
  status: 'active' | 'disabled';
}

interface CreditTransaction {
  id: string;
  amount: number;
  type: string;
  description: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  user_username: string;
  user_email: string;
  admin_id: string | null;
  admin_username: string | null;
}

export function CreditManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [adjustUserSearchTerm, setAdjustUserSearchTerm] = useState('');
  const [selectedAdjustUser, setSelectedAdjustUser] = useState<User | null>(null);
  const [creditAmount, setCreditAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [showAdjustUserList, setShowAdjustUserList] = useState(false);
  const [filteredAdjustUsers, setFilteredAdjustUsers] = useState<User[]>([]);
  const adjustUserSearchRef = useRef<HTMLDivElement>(null);

  // Transaction History section state
  const [selectedHistoryUser, setSelectedHistoryUser] = useState<User | null>(null);
  const [historyUserSearchTerm, setHistoryUserSearchTerm] = useState('');
  const [showHistoryUserList, setShowHistoryUserList] = useState(false);
  const [filteredHistoryUsers, setFilteredHistoryUsers] = useState<User[]>([]);
  const historyUserSearchRef = useRef<HTMLDivElement>(null);

  // Shared state
  const [users, setUsers] = useState<User[]>([]);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedHistoryUser) {
      fetchTransactions();
    } else {
      setTransactions([]);
      setTotalTransactions(0);
      setTotalPages(1);
      setCurrentPage(1);
    }
  }, [selectedHistoryUser, currentPage]);

  useEffect(() => {
    // Filter users for Adjust Credits section
    if (!adjustUserSearchTerm.trim()) {
      setFilteredAdjustUsers([]);
      return;
    }

    const searchTermLower = adjustUserSearchTerm.toLowerCase();
    const filtered = users.filter(user => 
      user.username.toLowerCase().includes(searchTermLower) ||
      user.email.toLowerCase().includes(searchTermLower)
    );
    setFilteredAdjustUsers(filtered);
  }, [adjustUserSearchTerm, users]);

  useEffect(() => {
    // Filter users for Transaction History section
    if (!historyUserSearchTerm.trim()) {
      setFilteredHistoryUsers([]);
      return;
    }

    const searchTermLower = historyUserSearchTerm.toLowerCase();
    const filtered = users.filter(user => 
      user.username.toLowerCase().includes(searchTermLower) ||
      user.email.toLowerCase().includes(searchTermLower)
    );
    setFilteredHistoryUsers(filtered);
  }, [historyUserSearchTerm, users]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (adjustUserSearchRef.current && !adjustUserSearchRef.current.contains(event.target as Node)) {
        setShowAdjustUserList(false);
      }
      if (historyUserSearchRef.current && !historyUserSearchRef.current.contains(event.target as Node)) {
        setShowHistoryUserList(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-active-users`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      showToast('Failed to load users', 'error');
    }
  };

  const fetchTransactions = async () => {
    if (!selectedHistoryUser) return;

    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-credit-transactions?limit=${itemsPerPage}&offset=${(currentPage - 1) * itemsPerPage}&user_id=${selectedHistoryUser.id}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch transactions');
      }

      const result = await response.json();
      setTransactions(result.data || []);
      setTotalTransactions(result.total || 0);
      setTotalPages(Math.ceil((result.total || 0) / itemsPerPage));

    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transaction history');
      showToast('Failed to load transaction history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustUserSelect = (user: User) => {
    setSelectedAdjustUser(user);
    setAdjustUserSearchTerm(`${user.username} (${user.email})`);
    setShowAdjustUserList(false);
  };

  const handleHistoryUserSelect = (user: User) => {
    setSelectedHistoryUser(user);
    setHistoryUserSearchTerm(`${user.username} (${user.email})`);
    setShowHistoryUserList(false);
  };

  const clearSelectedAdjustUser = () => {
    setSelectedAdjustUser(null);
    setAdjustUserSearchTerm('');
  };

  const clearSelectedHistoryUser = () => {
    setSelectedHistoryUser(null);
    setHistoryUserSearchTerm('');
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleCreditAdjustment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAdjustUser || !reason.trim()) return;

    try {
      setIsSubmitting(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/adjust-credits`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: selectedAdjustUser.id,
            amount: creditAmount,
            reason: reason.trim(),
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to adjust credits');
      }

      showToast('Credits adjusted successfully', 'success');
      
      // Reset form
      setSelectedAdjustUser(null);
      setAdjustUserSearchTerm('');
      setCreditAmount(0);
      setReason('');
      
      // Refresh data
      await Promise.all([
        fetchUsers(),
        fetchTransactions()
      ]);

    } catch (err) {
      console.error('Credit adjustment failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to adjust credits');
      showToast('Failed to adjust credits', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[--text-primary]">Credit Management</h1>
        <p className="mt-2 text-sm text-[--text-secondary]">
          Monitor and manage credit transactions
        </p>
      </div>

      {/* Credit Adjustment Form */}
      <div className="bg-[--bg-primary] shadow sm:rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-[--text-primary]">
            Adjust User Credits
          </h3>
          {error && (
            <Alert type="error" className="mt-4">
              {error}
            </Alert>
          )}
          <form onSubmit={handleCreditAdjustment} className="mt-5 space-y-4">
            <div ref={adjustUserSearchRef}>
              <label htmlFor="user" className="block text-sm font-medium text-[--text-primary]">
                Select User
              </label>
              <div className="mt-1 relative">
                <div className="relative">
                  <input
                    type="text"
                    value={adjustUserSearchTerm}
                    onChange={(e) => {
                      setAdjustUserSearchTerm(e.target.value);
                      setShowAdjustUserList(true);
                      if (!e.target.value) {
                        setSelectedAdjustUser(null);
                      }
                    }}
                    onFocus={() => setShowAdjustUserList(true)}
                    placeholder="Search by username or email"
                    className="block w-full pl-3 pr-10 py-2 border border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
                  />
                  {selectedAdjustUser && (
                    <button
                      type="button"
                      onClick={clearSelectedAdjustUser}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <X className="h-5 w-5 text-[--text-disabled]" />
                    </button>
                  )}
                </div>
                {showAdjustUserList && adjustUserSearchTerm && filteredAdjustUsers.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-[--bg-primary] shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                    {filteredAdjustUsers.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => handleAdjustUserSelect(user)}
                        className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-[--state-hover]"
                      >
                        <div className="flex items-center">
                          <span className="font-normal block truncate text-[--text-primary]">
                            {user.username} ({user.email})
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Fixed Credits Display */}
            {selectedAdjustUser && (
              <div className="bg-[--bg-secondary] rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-[--text-primary]">
                      Selected User
                    </h4>
                    <p className="text-sm text-[--text-primary] mt-1">
                      {selectedAdjustUser.username} ({selectedAdjustUser.email})
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-[--text-primary]">
                      Current Credits
                    </h4>
                    <p className="text-lg font-semibold text-[--link-default] mt-1">
                      {selectedAdjustUser.credits}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-[--text-primary]">
                Credit Amount (positive to add, negative to deduct)
              </label>
              <input
                type="number"
                id="amount"
                value={creditAmount}
                onChange={(e) => setCreditAmount(parseInt(e.target.value))}
                className="pl-3 mt-1 block w-full border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
              />
            </div>

            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-[--text-primary]">
                Description
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="pl-3 mt-1 block w-full border border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
                placeholder="Provide a reason for this credit adjustment"
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting || !selectedAdjustUser || !reason.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isSubmitting ? 'Processing...' : 'Apply Adjustment'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-[--bg-primary] shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-[--border-primary]">
          <h3 className="text-lg leading-6 font-medium text-[--text-primary]">
            Transaction History
          </h3>
          <div className="mt-3 relative" ref={historyUserSearchRef}>
            <div className="relative">
              <input
                type="text"
                value={historyUserSearchTerm}
                onChange={(e) => {
                  setHistoryUserSearchTerm(e.target.value);
                  setShowHistoryUserList(true);
                  if (!e.target.value) {
                    setSelectedHistoryUser(null);
                  }
                }}
                onFocus={() => setShowHistoryUserList(true)}
                placeholder="Search by username or email"
                className="block w-full pl-3 pr-10 py-2 border border-[--border-secondary] rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
              />
              {selectedHistoryUser && (
                <button
                  type="button"
                  onClick={clearSelectedHistoryUser}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X className="h-5 w-5 text-[--text-disabled]" />
                </button>
              )}
            </div>
            {showHistoryUserList && historyUserSearchTerm && filteredHistoryUsers.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-[--bg-primary] shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                {filteredHistoryUsers.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleHistoryUserSelect(user)}
                    className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-[--state-hover]"
                  >
                    <div className="flex items-center">
                      <span className="font-normal block truncate text-[--text-primary]">
                        {user.username} ({user.email})
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <table className="min-w-full divide-y divide-[--border-primary]">
          <thead className="bg-[--bg-secondary]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Credits
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Admin
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[--text-secondary] uppercase tracking-wider">
                Date
              </th>
            </tr>
          </thead>
          <tbody className="bg-[--bg-primary] divide-y divide-[--border-primary]">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-xs text-[--text-secondary]">
                  Loading transactions...
                </td>
              </tr>
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-xs text-[--text-secondary]">
                  {selectedHistoryUser ? 'No transactions found' : 'Select a user to view their transactions'}
                </td>
              </tr>
            ) : (
              transactions.map((transaction) => (
                <tr key={transaction.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-xs">
                      <div className="font-medium text-[--text-primary]">
                        {transaction.user_username}
                      </div>
                      <div className="text-[--text-secondary]">
                        {transaction.user_email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-[--text-secondary]">
                    {transaction.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs">
                    <span
                      className={clsx(
                        "inline-flex items-center",
                        transaction.amount > 0
                          ? "text-success-600 dark:text-success-400"
                          : "text-error-600 dark:text-error-400"
                      )}
                    >
                      {transaction.amount > 0 ? (
                        <ArrowUp className="w-3 h-3 mr-1" />
                      ) : (
                        <ArrowDown className="w-3 h-3 mr-1" />
                      )}
                      {Math.abs(transaction.amount)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-[--text-secondary]">
                    {transaction.admin_username || 'System'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-[--text-secondary]">
                    {format(new Date(transaction.created_at), 'MMM d, yyyy HH:mm')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
{selectedHistoryUser && transactions.length > 0 && (
  <div className="px-4 py-3 flex items-center justify-between border-t border-[--border-primary] sm:px-6">
    <div className="flex-1 flex justify-between sm:hidden">
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="relative inline-flex items-center px-4 py-2 border border-[--border-secondary] text-xs font-medium rounded-md text-[--text-primary] bg-[--bg-primary] hover:bg-[--state-hover] disabled:opacity-50"
      >
        Previous
      </button>
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="ml-3 relative inline-flex items-center px-4 py-2 border border-[--border-secondary] text-xs font-medium rounded-md text-[--text-primary] bg-[--bg-primary] hover:bg-[--state-hover] disabled:opacity-50"
      >
        Next
      </button>
    </div>
    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
      <div>
        <p className="text-xs text-[--text-primary]">
          <span className="font-medium">
            {Math.min((currentPage - 1) * itemsPerPage + 1, totalTransactions)}
          </span>{' '}
          to{' '}
          <span className="font-medium">
            {Math.min(currentPage * itemsPerPage, totalTransactions)}
          </span>{' '}
          of{' '}
          <span className="font-medium">{totalTransactions}</span>
        </p>
      </div>
      <div>
        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-1 py-1 rounded-l-md border border-[--border-secondary] bg-[--bg-primary] text-xs font-medium text-[--text-secondary] hover:bg-[--state-hover] disabled:opacity-50"
          >
            <span className="sr-only">Previous</span>
            <ChevronLeft className="h-3 w-3" />
          </button>
          {(() => {
            let startPage = Math.max(1, currentPage - 2);
            let endPage = Math.min(totalPages, startPage + 4);
            
            if (endPage - startPage < 4) {
              startPage = Math.max(1, endPage - 4);
            }
            
            return Array.from(
              { length: endPage - startPage + 1 }, 
              (_, i) => startPage + i
            ).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={clsx(
                  "relative inline-flex items-center px-2 py-1 border text-xs font-medium",
                  currentPage === page
                    ? "z-10 bg-[--state-selected] border-[--border-focus] text-[--link-default]"
                    : "bg-[--bg-primary] border-[--border-secondary] text-[--text-primary] hover:bg-[--state-hover]"
                )}
              >
                {page}
              </button>
            ));
          })()}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center px-1 py-1 rounded-r-md border border-[--border-secondary] bg-[--bg-primary] text-xs font-medium text-[--text-secondary] hover:bg-[--state-hover] disabled:opacity-50"
          >
            <span className="sr-only">Next</span>
            <ChevronRight className="h-3 w-3" />
          </button>
        </nav>
      </div>
    </div>
  </div>
)}

      </div>
    </div>
  );
}